// docs/58 — founding a company: the numbers and words the Outfitting panel, the forge and the lobby
// share. The rules are fk-world `founding.rs`'s; these mirror its constants for display only.
import type { FoundingPurse } from '../../bindings/types';

/** fk-world `RECRUIT_COST`: a second, third or fourth hero (FO2). */
export const RECRUIT_COST = 80;
/** fk-world `BOOST_COST`: one stat card trained into a hero (FO3). */
export const BOOST_COST = 25;
/** fk-world `founding::BASICS_SLOT`: the founding market's first basics-shelf slot (docs/159 SB2). */
export const BASICS_SLOT = 200;
/** fk-world `HEROES_PER_HUMAN` (FO8). */
export const MAX_HEROES = 4;

/** The stat cards a founding purse trains (fk-world `BOOST_CARDS`, the level-up stat cards). */
export const BOOSTS: ReadonlyArray<{ card: number; name: string; effect: string; short: string }> = [
  { card: 1, name: 'Hardy', effect: '+3 max HP', short: '+3 HP' },
  { card: 2, name: 'Bulwark', effect: '+1 guard', short: '+1 guard' },
  { card: 3, name: 'Keen edge', effect: '+1 weapon damage', short: '+1 dmg' },
  { card: 4, name: 'Attuned', effect: '+1 spell damage and heals', short: '+1 spell' },
  { card: 5, name: 'Fleet', effect: '+1 move', short: '+1 move' },
];

/** A purse as the panel reads it. A lobby member with no row yet (a lobby from before docs/58) has
 *  an untouched open purse; anyone else without one has none. */
export interface PurseView { open: boolean; left: number; total: number; spent: number; saved: number }

export function purseView(purse: Pick<FoundingPurse, 'open' | 'spent' | 'saved'> | undefined, startingGold: number, gathering: boolean): PurseView {
  if (!purse) return gathering ? { open: true, left: startingGold, total: startingGold, spent: 0, saved: 0 } : { open: false, left: 0, total: startingGold, spent: 0, saved: 0 };
  return { open: purse.open, left: Math.max(0, startingGold - purse.spent), total: startingGold, spent: purse.spent, saved: purse.saved };
}

/** What forging one more hero costs and whether it may be done (the server's `recruit_fee`). */
export interface Recruit { fee: number; ok: boolean; why?: string }

export function recruitState(heroes: number, purse: PurseView): Recruit {
  if (heroes === 0) return { fee: 0, ok: true };
  if (heroes >= MAX_HEROES) return { fee: RECRUIT_COST, ok: false, why: `A company is at most ${MAX_HEROES} heroes.` };
  if (!purse.open) return { fee: RECRUIT_COST, ok: false, why: 'Your company has set out: more heroes join it by hiring in the villages.' };
  if (purse.left < RECRUIT_COST) return { fee: RECRUIT_COST, ok: false, why: `Recruiting costs ${RECRUIT_COST} founding gold; you have ${purse.left}.` };
  return { fee: RECRUIT_COST, ok: true };
}

/** "Saved for later" line: where the rest of the purse goes. */
export function savedLine(left: number, carrier: string | undefined, gathering: boolean): string {
  const when = gathering ? 'when the World begins' : 'when you set out';
  if (!carrier) return `Whatever you do not spend is saved ${when}, in your first hero's purse.`;
  return left > 0 ? `${left} left goes to ${carrier}'s purse ${when}, as plain gold.` : `Every coin is spent.`;
}

/* ---------- docs/146: gear while outfitting ---------- */

/** fk-world `outfitting_window`: a player may remake their heroes (and nothing is sold, and they
 *  trade only between their own heroes) while the lobby gathers (phase 0) or their purse is open. */
export function outfittingWindow(phase: number | undefined, purse: Pick<FoundingPurse, 'open'> | undefined): boolean {
  return phase === 0 || !!purse?.open;
}

/** fk-world `outfitting_trade_block`: why two players' heroes cannot trade yet, or undefined. */
export function tradeBlock(o: { sameOwner: boolean; gathering: boolean; fromName: string; toName: string; fromOutfitting: boolean; toOutfitting: boolean }): string | undefined {
  if (o.sameOwner) return undefined;
  if (o.gathering) return 'Players trade once the World begins.';
  if (o.fromOutfitting) return `${o.fromName} trades with other players once your company sets out.`;
  if (o.toOutfitting) return `${o.toName}'s company is still outfitting: trade once they set out.`;
  return undefined;
}
